import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalProgress } from "../../features/portal/portal-slice";
import { fetchPortalProgress } from "../../features/portal/slice-extensions/portal-slice-extension";
import MemberProgressPanel from "../training/MemberProgressPanel";
import PortalRecordRow from "./PortalRecordRow";

interface PortalProgressCardProps {
  memberId: string;
}

/**
 * The member's (or ward's) training Progress, read-only, with the training record under each
 * licence-class enrolment. The page title already says "Progress", so the card carries no heading.
 */
const PortalProgressCard = ({ memberId }: PortalProgressCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const progress = useAppSelector(selectPortalProgress(memberId));

  useEffect(() => {
    dispatch(fetchPortalProgress(memberId));
  }, [dispatch, memberId]);

  if (!progress) return null;
  if (progress.length === 0) {
    return <p className="mb-6 text-sm text-grey-40 dark:text-grey-80">{t("portal.progress.empty")}</p>;
  }

  return (
    <div className="with-border mb-6 rounded-2xl bg-white p-4 dark:bg-primary-10" data-testid="portal-progress">
      <div className="space-y-4">
        {progress.map((entry) => (
          <div key={entry.enrolmentId}>
            <p className="font-medium text-grey-20 dark:text-white">{entry.programmeName}</p>
            <MemberProgressPanel progress={entry} />
            <PortalRecordRow
              memberId={memberId}
              enrolmentId={entry.enrolmentId}
              recordAvailable={entry.recordAvailable}
              signedOn={entry.signedOn}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortalProgressCard;
